import { memo } from "react";

const THEMES = {
  minimal: {
    accent: "#27272A",
    ink: "#18181B",
    muted: "#52525B",
    line: "#D4D4D8",
    paper: "#FFFFFF",
    font: "Arial, Helvetica, sans-serif",
    header: "plain",
    section: "rule",
    skills: "text",
    order: [
      "summary",
      "experience",
      "education",
      "skills",
      "certifications",
      "projects",
      "languages",
    ],
  },

  modern: {
    accent: "#2563EB",
    ink: "#0F172A",
    muted: "#475569",
    line: "#DBEAFE",
    paper: "#FFFFFF",
    font: "Arial, Helvetica, sans-serif",
    header: "modern",
    section: "bar",
    skills: "pill",
    order: [
      "summary",
      "experience",
      "skills",
      "education",
      "projects",
      "certifications",
      "languages",
    ],
  },

  engineering: {
    accent: "#0891B2",
    ink: "#0F172A",
    muted: "#475569",
    line: "#CFFAFE",
    paper: "#FFFFFF",
    font: "Arial, Helvetica, sans-serif",
    header: "technical",
    section: "technical",
    skills: "technical",
    order: [
      "summary",
      "skills",
      "experience",
      "projects",
      "education",
      "certifications",
      "languages",
    ],
  },

  executive: {
    accent: "#1E293B",
    ink: "#0F172A",
    muted: "#64748B",
    line: "#CBD5E1",
    paper: "#FFFFFF",
    font: "Georgia, Times New Roman, serif",
    header: "centered",
    section: "classic",
    skills: "text",
    order: [
      "summary",
      "experience",
      "education",
      "skills",
      "certifications",
      "languages",
      "projects",
    ],
  },

  graduate: {
    accent: "#059669",
    ink: "#0F172A",
    muted: "#475569",
    line: "#A7F3D0",
    paper: "#FFFFFF",
    font: "Arial, Helvetica, sans-serif",
    header: "graduate",
    section: "graduate",
    skills: "pill",
    order: [
      "summary",
      "education",
      "projects",
      "skills",
      "experience",
      "certifications",
      "languages",
    ],
  },

  swiss: {
    accent: "#DC2626",
    ink: "#111827",
    muted: "#4B5563",
    line: "#FECACA",
    paper: "#FFFFFF",
    font: "Arial, Helvetica, sans-serif",
    header: "swiss",
    section: "numbered",
    skills: "text",
    order: [
      "summary",
      "experience",
      "skills",
      "education",
      "projects",
      "certifications",
      "languages",
    ],
  },

  consulting: {
    accent: "#7C3AED",
    ink: "#18181B",
    muted: "#52525B",
    line: "#DDD6FE",
    paper: "#FFFFFF",
    font: "Arial, Helvetica, sans-serif",
    header: "consulting",
    section: "numberedRule",
    skills: "text",
    order: [
      "summary",
      "experience",
      "education",
      "skills",
      "projects",
      "certifications",
      "languages",
    ],
  },

  elegant: {
    accent: "#7C5A36",
    ink: "#292524",
    muted: "#57534E",
    line: "#D6D3D1",
    paper: "#FFFFFF",
    font: "Georgia, Times New Roman, serif",
    header: "elegant",
    section: "ornate",
    skills: "text",
    order: [
      "summary",
      "experience",
      "education",
      "skills",
      "certifications",
      "projects",
      "languages",
    ],
  },

  tech: {
    accent: "#0F766E",
    ink: "#134E4A",
    muted: "#475569",
    line: "#99F6E4",
    paper: "#FFFFFF",
    font: "Arial, Helvetica, sans-serif",
    header: "tech",
    section: "tech",
    skills: "code",
    order: [
      "summary",
      "skills",
      "experience",
      "projects",
      "education",
      "certifications",
      "languages",
    ],
  },

  creative: {
    accent: "#DB2777",
    ink: "#1F2937",
    muted: "#4B5563",
    line: "#FBCFE8",
    paper: "#FFFFFF",
    font: "Arial, Helvetica, sans-serif",
    header: "creative",
    section: "creative",
    skills: "pill",
    order: [
      "summary",
      "experience",
      "skills",
      "education",
      "projects",
      "certifications",
      "languages",
    ],
  },
};

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const has = (value) =>
  clean(value).length > 0;

const list = (value) =>
  Array.isArray(value) ? value : [];

const range = (item) => {
  const start = clean(item?.start);

  const end = item?.current
    ? "Present"
    : clean(item?.end);

  return [start, end]
    .filter(Boolean)
    .join(" – ");
};

const ContactLine = ({
  data,
  theme,
}) => {
  const contacts = [
    data.email,
    data.phone,
    data.location,
    data.linkedin,
  ]
    .map(clean)
    .filter(Boolean);

  if (!contacts.length) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] leading-tight"
      style={{
        color: theme.muted,
      }}
    >
      {contacts.map(
        (item, index) => (
          <span
            key={`${item}-${index}`}
          >
            {item}
          </span>
        )
      )}
    </div>
  );
};

const Header = ({
  data,
  theme,
}) => {
  const name =
    clean(data.full_name) ||
    "Your Name";

  const role =
    clean(data.target_role);

  if (
    theme.header ===
      "centered" ||
    theme.header ===
      "elegant"
  ) {
    return (
      <header
        className="text-center pb-4 mb-5"
        style={{
          borderBottom:
            `1px solid ${theme.accent}`,
        }}
      >
        <h1
          className="text-[25px] font-bold tracking-[0.02em]"
          style={{
            color: theme.ink,
            fontFamily: theme.font,
          }}
        >
          {name}
        </h1>

        {role && (
          <div
            className="mt-1 text-[10px] uppercase tracking-[0.22em]"
            style={{
              color: theme.accent,
            }}
          >
            {role}
          </div>
        )}

        <div className="mt-2 flex justify-center">
          <ContactLine
            data={data}
            theme={theme}
          />
        </div>
      </header>
    );
  }

  if (
    theme.header ===
    "swiss"
  ) {
    return (
      <header className="pb-4 mb-5">
        <div className="flex items-end justify-between gap-5">
          <div className="min-w-0">
            <h1
              className="text-[27px] leading-none font-bold tracking-tight"
              style={{
                color: theme.ink,
              }}
            >
              {name}
            </h1>

            {role && (
              <div
                className="mt-2 text-[10px] font-bold uppercase tracking-[0.18em]"
                style={{
                  color: theme.accent,
                }}
              >
                {role}
              </div>
            )}
          </div>

          <div className="shrink-0 text-right max-w-[48%]">
            <ContactLine
              data={data}
              theme={theme}
            />
          </div>
        </div>

        <div
          className="mt-4 h-[3px] w-full"
          style={{
            background:
              theme.accent,
          }}
        />
      </header>
    );
  }

  if (
    theme.header ===
    "consulting"
  ) {
    return (
      <header className="pb-4 mb-5">
        <div className="flex items-end justify-between gap-5">
          <h1
            className="text-[24px] leading-none font-bold"
            style={{
              color: theme.ink,
            }}
          >
            {name}
          </h1>

          {role && (
            <div
              className="text-[10px] font-bold text-right max-w-[45%]"
              style={{
                color: theme.accent,
              }}
            >
              {role}
            </div>
          )}
        </div>

        <div className="mt-2">
          <ContactLine
            data={data}
            theme={theme}
          />
        </div>

        <div
          className="mt-3 h-[1px]"
          style={{
            background:
              theme.accent,
          }}
        />
      </header>
    );
  }

  if (
    theme.header ===
    "technical"
  ) {
    return (
      <header
        className="pl-4 pb-4 mb-5"
        style={{
          borderLeft:
            `4px solid ${theme.accent}`,
        }}
      >
        <h1
          className="text-[24px] leading-none font-bold"
          style={{
            color: theme.ink,
          }}
        >
          {name}
        </h1>

        {role && (
          <div
            className="mt-2 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{
              color: theme.accent,
            }}
          >
            {role}
          </div>
        )}

        <div className="mt-2">
          <ContactLine
            data={data}
            theme={theme}
          />
        </div>
      </header>
    );
  }

  if (
    theme.header ===
    "tech"
  ) {
    return (
      <header
        className="p-4 mb-5 rounded"
        style={{
          background:
            theme.line + "55",
          borderLeft:
            `5px solid ${theme.accent}`,
        }}
      >
        <h1
          className="text-[23px] leading-none font-bold"
          style={{
            color: theme.ink,
          }}
        >
          {name}
        </h1>

        {role && (
          <div
            className="mt-2 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{
              color: theme.accent,
            }}
          >
            {role}
          </div>
        )}

        <div className="mt-2">
          <ContactLine
            data={data}
            theme={theme}
          />
        </div>
      </header>
    );
  }

  if (
    theme.header ===
    "creative"
  ) {
    return (
      <header
        className="relative overflow-hidden p-4 mb-5 rounded-xl"
        style={{
          background:
            theme.line + "55",
        }}
      >
        <div
          className="absolute right-5 top-4 w-7 h-7 rounded-full"
          style={{
            background:
              theme.accent,
          }}
        />

        <h1
          className="text-[24px] leading-none font-bold pr-10"
          style={{
            color: theme.ink,
          }}
        >
          {name}
        </h1>

        {role && (
          <div
            className="mt-2 text-[10px] font-bold"
            style={{
              color: theme.accent,
            }}
          >
            {role}
          </div>
        )}

        <div className="mt-2 pr-8">
          <ContactLine
            data={data}
            theme={theme}
          />
        </div>
      </header>
    );
  }

  return (
    <header
      className="pb-4 mb-5"
      style={{
        borderBottom:
          `2px solid ${theme.accent}`,
      }}
    >
      <h1
        className="text-[25px] leading-none font-bold tracking-tight"
        style={{
          color: theme.ink,
        }}
      >
        {name}
      </h1>

      {role && (
        <div
          className="mt-2 text-[10px] font-bold"
          style={{
            color: theme.accent,
          }}
        >
          {role}
        </div>
      )}

      <div className="mt-2">
        <ContactLine
          data={data}
          theme={theme}
        />
      </div>
    </header>
  );
};

const Section = ({
  title,
  number,
  theme,
  children,
}) => {
  const label =
    clean(title);

  if (
    theme.section ===
      "numbered" ||
    theme.section ===
      "numberedRule"
  ) {
    return (
      <section className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="text-[9px] font-bold"
            style={{
              color:
                theme.accent,
            }}
          >
            {String(number).padStart(
              2,
              "0"
            )}
          </span>

          <h2
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{
              color: theme.ink,
            }}
          >
            {label}
          </h2>

          <div
            className="flex-1 h-px"
            style={{
              background:
                theme.line,
            }}
          />
        </div>

        <div
          className="text-[11px] leading-[1.38]"
          style={{
            color: theme.ink,
          }}
        >
          {children}
        </div>
      </section>
    );
  }

  if (
    theme.section ===
      "technical" ||
    theme.section ===
      "tech"
  ) {
    return (
      <section className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="w-[3px] h-4 shrink-0"
            style={{
              background:
                theme.accent,
            }}
          />

          <h2
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{
              color:
                theme.accent,
            }}
          >
            {label}
          </h2>

          <div
            className="flex-1 h-px"
            style={{
              background:
                theme.line,
            }}
          />
        </div>

        <div
          className="text-[11px] leading-[1.38]"
          style={{
            color: theme.ink,
          }}
        >
          {children}
        </div>
      </section>
    );
  }

  if (
    theme.section ===
    "graduate"
  ) {
    return (
      <section className="mb-4">
        <h2
          className="text-[11px] font-bold"
          style={{
            color:
              theme.accent,
          }}
        >
          {label}
        </h2>

        <div
          className="mt-1 w-12 h-[2px]"
          style={{
            background:
              theme.accent,
          }}
        />

        <div
          className="mt-2 text-[11px] leading-[1.38]"
          style={{
            color: theme.ink,
          }}
        >
          {children}
        </div>
      </section>
    );
  }

  if (
    theme.section ===
      "classic" ||
    theme.section ===
      "ornate"
  ) {
    return (
      <section className="mb-4">
        <h2
          className="text-[10px] font-bold uppercase tracking-[0.18em]"
          style={{
            color:
              theme.accent,
          }}
        >
          {label}
        </h2>

        <div
          className="mt-1 h-px"
          style={{
            background:
              theme.line,
          }}
        />

        <div
          className="mt-2 text-[11px] leading-[1.4]"
          style={{
            color: theme.ink,
          }}
        >
          {children}
        </div>
      </section>
    );
  }

  if (
    theme.section ===
    "creative"
  ) {
    return (
      <section className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="w-3 h-3 rounded-full shrink-0"
            style={{
              background:
                theme.accent,
            }}
          />

          <h2
            className="text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{
              color:
                theme.accent,
            }}
          >
            {label}
          </h2>

          <div
            className="flex-1 h-px"
            style={{
              background:
                theme.accent,
            }}
          />
        </div>

        <div
          className="text-[11px] leading-[1.38]"
          style={{
            color: theme.ink,
          }}
        >
          {children}
        </div>
      </section>
    );
  }

  return (
    <section className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <h2
          className="text-[10px] font-bold uppercase tracking-[0.2em]"
          style={{
            color:
              theme.accent,
          }}
        >
          {label}
        </h2>

        <div
          className="flex-1 h-px"
          style={{
            background:
              theme.line,
          }}
        />
      </div>

      <div
        className="text-[11px] leading-[1.38]"
        style={{
          color: theme.ink,
        }}
      >
        {children}
      </div>
    </section>
  );
};

const Experience = ({
  items,
  theme,
}) => (
  <div>
    {list(items).map(
      (item, index) => {
        const title =
          [
            clean(item.title),
            clean(item.company),
          ]
            .filter(Boolean)
            .join(" — ") ||
          "Role";

        const dates =
          range(item);

        return (
          <div
            key={`${title}-${index}`}
            className="mb-3 last:mb-0"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div
                  className="font-bold text-[11px] leading-tight"
                  style={{
                    color:
                      theme.ink,
                  }}
                >
                  {title}
                </div>

                {has(
                  item.location
                ) && (
                  <div
                    className="mt-0.5 text-[9.5px] italic"
                    style={{
                      color:
                        theme.muted,
                    }}
                  >
                    {item.location}
                  </div>
                )}
              </div>

              {dates && (
                <div
                  className="shrink-0 max-w-[30%] text-right text-[9px] leading-tight"
                  style={{
                    color:
                      theme.muted,
                  }}
                >
                  {dates}
                </div>
              )}
            </div>

            {list(
              item.bullets
            ).filter(Boolean)
              .length > 0 && (
              <ul className="mt-1 pl-4 list-disc space-y-0.5">
                {list(
                  item.bullets
                )
                  .filter(Boolean)
                  .map(
                    (
                      bullet,
                      i
                    ) => (
                      <li
                        key={`${index}-${i}`}
                      >
                        {bullet}
                      </li>
                    )
                  )}
              </ul>
            )}
          </div>
        );
      }
    )}
  </div>
);

const Education = ({
  items,
  theme,
}) => (
  <div>
    {list(items).map(
      (item, index) => {
        const title =
          [
            clean(item.degree),
            clean(item.school),
          ]
            .filter(Boolean)
            .join(" — ") ||
          "Degree";

        const dates =
          range(item);

        const meta =
          [
            clean(item.location),
            has(item.gpa)
              ? `GPA ${clean(
                  item.gpa
                )}`
              : "",
          ]
            .filter(Boolean)
            .join(" · ");

        return (
          <div
            key={`${title}-${index}`}
            className="mb-2 last:mb-0"
          >
            <div className="flex items-start gap-3">
              <div
                className="flex-1 min-w-0 font-bold text-[11px] leading-tight"
                style={{
                  color:
                    theme.ink,
                }}
              >
                {title}
              </div>

              {dates && (
                <div
                  className="shrink-0 max-w-[30%] text-right text-[9px]"
                  style={{
                    color:
                      theme.muted,
                  }}
                >
                  {dates}
                </div>
              )}
            </div>

            {meta && (
              <div
                className="mt-0.5 text-[9.5px] italic"
                style={{
                  color:
                    theme.muted,
                }}
              >
                {meta}
              </div>
            )}
          </div>
        );
      }
    )}
  </div>
);

const Skills = ({
  items,
  theme,
}) => {
  const skills = list(items)
    .map(clean)
    .filter(Boolean);

  if (
    theme.skills ===
      "text" ||
    theme.skills ===
      "technical" ||
    theme.skills ===
      "code"
  ) {
    return (
      <div>
        {skills.join(" · ")}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {skills.map(
        (skill) => (
          <span
            key={skill}
            className="px-2 py-0.5 rounded text-[9.5px]"
            style={{
              background:
                theme.line +
                "66",
              color:
                theme.accent,
            }}
          >
            {skill}
          </span>
        )
      )}
    </div>
  );
};

const Certifications = ({
  items,
}) => (
  <ul className="list-disc pl-4 space-y-0.5">
    {list(items).map(
      (item, index) => {
        const value =
          [
            clean(item.name),
            clean(item.issuer),
            clean(item.year),
          ]
            .filter(Boolean)
            .join(" — ");

        return value ? (
          <li
            key={`${value}-${index}`}
          >
            {value}
          </li>
        ) : null;
      }
    )}
  </ul>
);

const Projects = ({
  items,
  theme,
}) => (
  <div>
    {list(items).map(
      (item, index) => (
        <div
          key={`${clean(
            item.name
          )}-${index}`}
          className="mb-2 last:mb-0"
        >
          <div
            className="font-bold text-[11px]"
            style={{
              color:
                theme.ink,
            }}
          >
            {clean(
              item.name
            ) || "Project"}
          </div>

          {has(
            item.description
          ) && (
            <div className="mt-0.5">
              {item.description}
            </div>
          )}

          {(has(item.tech) ||
            has(item.link)) && (
            <div
              className="mt-0.5 text-[9px] italic"
              style={{
                color:
                  theme.muted,
              }}
            >
              {[
                clean(item.tech),
                clean(item.link),
              ]
                .filter(Boolean)
                .join("  |  ")}
            </div>
          )}
        </div>
      )
    )}
  </div>
);

const Languages = ({
  items,
}) => (
  <div>
    {list(items)
      .map((item) =>
        has(item.name)
          ? `${clean(
              item.name
            )}${
              has(item.level)
                ? ` (${clean(
                    item.level
                  )})`
                : ""
            }`
          : ""
      )
      .filter(Boolean)
      .join(" · ")}
  </div>
);

function ResumePreviewBase({
  resume,
}) {
  const data =
    resume?.data || {};

  const theme =
    THEMES[
      resume?.template
    ] || THEMES.modern;

  const sections = {
    summary: has(
      data.summary
    ) ? (
      <Section
        title="Professional Summary"
        number={1}
        theme={theme}
      >
        <p>
          {data.summary}
        </p>
      </Section>
    ) : null,

    experience:
      list(
        data.experience
      ).length ? (
        <Section
          title="Work Experience"
          number={2}
          theme={theme}
        >
          <Experience
            items={
              data.experience
            }
            theme={theme}
          />
        </Section>
      ) : null,

    education:
      list(
        data.education
      ).length ? (
        <Section
          title="Education"
          number={3}
          theme={theme}
        >
          <Education
            items={
              data.education
            }
            theme={theme}
          />
        </Section>
      ) : null,

    skills:
      list(
        data.skills
      ).length ? (
        <Section
          title="Skills"
          number={4}
          theme={theme}
        >
          <Skills
            items={
              data.skills
            }
            theme={theme}
          />
        </Section>
      ) : null,

    certifications:
      list(
        data.certifications
      ).length ? (
        <Section
          title="Certifications"
          number={5}
          theme={theme}
        >
          <Certifications
            items={
              data.certifications
            }
          />
        </Section>
      ) : null,

    projects:
      list(
        data.projects
      ).length ? (
        <Section
          title="Projects"
          number={6}
          theme={theme}
        >
          <Projects
            items={
              data.projects
            }
            theme={theme}
          />
        </Section>
      ) : null,

    languages:
      list(
        data.languages
      ).length ? (
        <Section
          title="Languages"
          number={7}
          theme={theme}
        >
          <Languages
            items={
              data.languages
            }
          />
        </Section>
      ) : null,
  };

  return (
    <div
      data-testid="resume-preview"
      className="bg-white text-slate-900 mx-auto"
      style={{
        width: "210mm",
        minHeight: "297mm",
        padding: "15mm",
        boxSizing:
          "border-box",
        fontFamily:
          theme.font,
        background:
          theme.paper,
        color: theme.ink,
        boxShadow:
          "0 20px 60px rgba(0,0,0,.35)",
      }}
    >
      <Header
        data={data}
        theme={theme}
      />

      {(theme.order || [])
        .map((key) =>
          sections[key] ? (
            <div key={key}>
              {sections[key]}
            </div>
          ) : null
        )}
    </div>
  );
}

export default memo(
  ResumePreviewBase
);
